import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import XIcon from '../../icons/IconLib/XIcon'

export interface ImageViewerProps {
  /** URL снимка. */
  src: string
  /** Подпись: alt картинки и aria-label диалога. Без неё диалог зовётся VIEWER_LABEL. */
  alt?: string
  /** Закрытие: бэкдроп, крестик, Escape. */
  onClose: () => void
}

const CLOSE_LABEL = 'סגירה'
const VIEWER_LABEL = 'תצוגת תמונה'

/**
 * Полноэкранный просмотр снимка. Рендерится порталом в document.body, поэтому
 * перекрывает любую модалку и sticky-шапку, а не только своего предка.
 */
const ImageViewer: React.FC<ImageViewerProps> = ({ src, alt, onClose }) => {
  const closeRef = useRef<HTMLButtonElement>(null)

  // Фокус уводим на крестик, а при закрытии возвращаем туда, откуда открыли:
  // иначе после Escape фокус улетает в body и Tab начинает со начала страницы.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    closeRef.current?.focus()
    return () => opener?.focus?.()
  }, [])

  /**
   * Escape слушаем на document в capture-фазе и обрываем всплытие: AppModal
   * держит свой обработчик на window в bubble-фазе, и без этого один Escape
   * закрыл бы и снимок, и модалку под ним.
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      onClose()
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [onClose])

  return createPortal(
    <Backdrop
      role='dialog'
      aria-modal='true'
      aria-label={alt || VIEWER_LABEL}
      // Только по самому фону: клик по снимку или крестику всплывает сюда же.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <CloseButton
        ref={closeRef}
        type='button'
        onClick={onClose}
        aria-label={CLOSE_LABEL}
        title={CLOSE_LABEL}
      >
        <XIcon size={16} />
      </CloseButton>
      <Image src={src} alt={alt} />
    </Backdrop>,
    document.body,
  )
}

export default ImageViewer

/**
 * `position: fixed` + `inset: 0` держит слой ровно во вьюпорте, `overflow: hidden`
 * не даёт снимку растянуть страницу. `direction: rtl` задан явно: у html нет
 * dir, а портал в body не наследует его от предка-модалки.
 */
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 1.25rem;
  overflow: hidden;
  direction: rtl;
  background: rgba(0, 0, 0, 0.82);
`

const Image = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: ${({ theme }) => theme.radii.md};
  display: block;
`

const CloseButton = styled.button`
  position: absolute;
  top: 0.75rem;
  inset-inline-end: 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.ink2};
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    background: ${({ theme }) => theme.colors.line2};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }
`
